import { useState, useEffect, useCallback, useRef } from 'react';
import { getAnalyticsApiBaseUrl } from '../../../config/api';
import { fetchWithAuth } from '../../../utils/auth';
import { PeriodConfig } from '../types';

function buildQueryString(period: PeriodConfig): string {
  if (period.type === 'live') return '';
  if (period.type === 'period') return `?period=${period.days}`;
  if (period.type === 'custom') return `?from=${period.from}&to=${period.to}`;
  return '';
}

// Endpoints that have proper snapshot support with live-compatible response format
const SNAPSHOT_READY_ENDPOINTS = new Set([
  'revenue-dashboard',
]);

export interface UseAnalyticsDataResult {
  data: Record<string, any>;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  isLive: boolean;
}

export function useAnalyticsData(
  endpoints: string[],
  period: PeriodConfig,
): UseAnalyticsDataResult {
  const [data, setData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = getAnalyticsApiBaseUrl();
      const qs = buildQueryString(period);

      const results = await Promise.allSettled(
        endpoints.map(async (endpoint) => {
          // Only use period query for snapshot-ready endpoints
          // All others always get live data
          const useSnapshot = qs && SNAPSHOT_READY_ENDPOINTS.has(endpoint);
          const url = `${base}/analytics/${endpoint}${useSnapshot ? qs : ''}`;

          const res = await fetchWithAuth(url);
          if (res.ok) {
            return { endpoint, data: await res.json() };
          }

          // If snapshot fails, fall back to live
          if (useSnapshot) {
            const fallbackRes = await fetchWithAuth(`${base}/analytics/${endpoint}`);
            if (fallbackRes.ok) {
              return { endpoint, data: await fallbackRes.json() };
            }
          }

          throw new Error(`${endpoint}: ${res.status}`);
        }),
      );

      const newData: Record<string, any> = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          newData[r.value.endpoint] = r.value.data;
        }
      });

      setData(newData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [endpoints.join(','), period]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Listen for a page-wide refresh signal fired by the Analytics V2 header's
  // Refresh button. Lets us keep the button in the header once — no need to
  // drill a refreshKey prop through every tab.
  useEffect(() => {
    const handler = () => fetchAll();
    window.addEventListener(ANALYTICS_REFRESH_EVENT, handler);
    return () => window.removeEventListener(ANALYTICS_REFRESH_EVENT, handler);
  }, [fetchAll]);

  // Auto-refresh every 5 min for live mode only
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (period.type === 'live') {
      intervalRef.current = setInterval(fetchAll, 5 * 60 * 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [period.type, fetchAll]);

  return { data, loading, error, refresh: fetchAll, isLive: period.type === 'live' };
}

export const ANALYTICS_REFRESH_EVENT = 'analytics-v2:refresh';

/** Trigger a refresh across every useAnalyticsData hook mounted on the page. */
export function triggerAnalyticsRefresh(): void {
  window.dispatchEvent(new CustomEvent(ANALYTICS_REFRESH_EVENT));
}
